/* @section: generator-engine */
import type { ColumnDef, GeneratedTable } from "./types";

/* ----------------------------------------------------------------------------
 * Utilitaires de données d'exemple (déterministes mais variés)
 * ------------------------------------------------------------------------- */

const PRODUITS = ["Ordinateur portable", "Souris sans fil", "Clavier mécanique", "Écran 27\"", "Casque audio", "Webcam HD", "Disque SSD", "Imprimante", "Routeur Wi-Fi", "Tablette", "Chargeur USB-C", "Enceinte Bluetooth"];
const CLIENTS = ["Dupont SARL", "Martin & Fils", "Boutique Léa", "TechPro SAS", "Café du Centre", "Garage Moreau", "Pharmacie Rivoli", "Studio Nova", "Atelier Bois+", "Épicerie Bio"];
const PRENOMS = ["Marie", "Jean", "Sophie", "Karim", "Lucas", "Fatou", "Pierre", "Inès", "Thomas", "Awa", "Hugo", "Nadia"];
const NOMS = ["Bernard", "Dubois", "Lefebvre", "Diallo", "Moreau", "Garcia", "Roux", "Traoré", "Petit", "Nguyen"];
const POSTES = ["Vendeur", "Comptable", "Chef de projet", "Technicien", "Assistant RH", "Commercial", "Manager", "Magasinier"];
const CONTRATS = ["CDI", "CDD", "Intérim", "Stage", "Alternance"];
const STATUTS_CONGE = ["Approuvé", "En attente", "Refusé"];
const TYPES_CONGE = ["Congés payés", "RTT", "Maladie", "Sans solde", "Familial"];
const CHANTIERS_TACHES = ["Terrassement", "Fondations", "Gros œuvre", "Charpente", "Couverture", "Plomberie", "Électricité", "Cloisons", "Peinture", "Finitions"];
const MATERIAUX = ["Ciment (sac)", "Sable (m³)", "Gravier (m³)", "Parpaing (u)", "Acier (kg)", "Bois (m³)", "Carrelage (m²)", "Peinture (L)", "Tuiles (u)", "Câble élec. (m)"];
const STATUTS_TACHE = ["À faire", "En cours", "Terminé", "Bloqué"];

function pick<T>(arr: T[], i: number): T {
  return arr[i % arr.length];
}
function dateStr(offsetDays: number): string {
  const d = new Date(2026, 0, 1);
  d.setDate(d.getDate() + offsetDays);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
function rnd(seed: number, min: number, max: number): number {
  // pseudo-aléatoire déterministe
  const x = Math.sin(seed * 9973) * 10000;
  const f = x - Math.floor(x);
  return Math.round((min + f * (max - min)) * 100) / 100;
}

/* ----------------------------------------------------------------------------
 * Helpers de colonnes
 * ------------------------------------------------------------------------- */
const col = (name: string, type: ColumnDef["type"], extra: Partial<ColumnDef> = {}): ColumnDef => ({
  name,
  type,
  ...extra,
});

/** Convertit un index de colonne (0-based) en lettre Excel (A, B, ... Z, AA...) */
export function colLetter(index: number): string {
  let s = "";
  let n = index;
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

/* ----------------------------------------------------------------------------
 * Définition des modèles par tâche
 * Chaque modèle renvoie colonnes + générateur de ligne
 * ------------------------------------------------------------------------- */

interface Template {
  title: string;
  columns: ColumnDef[];
  /** génère la i-ème ligne (0-based) ; les cellules formule peuvent être vides ("") */
  row: (i: number, cols: ColumnDef[]) => (string | number)[];
  note?: string;
}

/** Trouve l'index d'une colonne par son nom (pour bâtir les formules) */
function idxOf(cols: ColumnDef[], name: string): number {
  return cols.findIndex((c) => c.name === name);
}

const TEMPLATES: Record<string, Template> = {
  /* ---------------- COMMERCE ---------------- */
  "suivi-ventes": {
    title: "Suivi des ventes",
    columns: [
      col("Date", "date", { width: 12 }),
      col("Produit", "text", { width: 22 }),
      col("Client", "text", { width: 20 }),
      col("Quantité", "number", { width: 10 }),
      col("Prix unitaire", "currency", { width: 14 }),
      col("Total HT", "formula", { width: 14, formula: "=D{row}*E{row}" }),
    ],
    row: (i) => [dateStr(i * 2), pick(PRODUITS, i), pick(CLIENTS, i + 3), 1 + (i % 9), rnd(i + 1, 9.9, 899), ""],
    note: "La colonne « Total HT » est calculée automatiquement (Quantité × Prix unitaire).",
  },
  "gestion-stocks": {
    title: "Gestion des stocks",
    columns: [
      col("Référence", "text", { width: 12 }),
      col("Produit", "text", { width: 22 }),
      col("Stock actuel", "number", { width: 12 }),
      col("Seuil d'alerte", "number", { width: 13 }),
      col("Prix unitaire", "currency", { width: 14 }),
      col("Valeur stock", "formula", { width: 14, formula: "=C{row}*E{row}" }),
    ],
    row: (i) => [`REF-${String(1000 + i)}`, pick(PRODUITS, i), 5 + ((i * 7) % 120), 10 + (i % 5) * 5, rnd(i + 2, 4.5, 650), ""],
    note: "« Valeur stock » = Stock actuel × Prix unitaire.",
  },
  "tableau-clients": {
    title: "Tableau de bord clients",
    columns: [
      col("Client", "text", { width: 22 }),
      col("Ville", "text", { width: 16 }),
      col("Email", "text", { width: 26 }),
      col("Nb commandes", "number", { width: 13 }),
      col("CA total", "currency", { width: 14 }),
      col("Panier moyen", "formula", { width: 14, formula: "=IF(D{row}=0,0,E{row}/D{row})" }),
    ],
    row: (i) => {
      const c = pick(CLIENTS, i);
      const slug = c.toLowerCase().replace(/[^a-z]/g, "");
      return [c, pick(["Paris", "Lyon", "Lille", "Marseille", "Nantes", "Bordeaux"], i), `contact@${slug}.fr`, 1 + (i % 30), rnd(i + 3, 120, 15000), ""];
    },
    note: "« Panier moyen » = CA total ÷ Nombre de commandes.",
  },

  /* ---------------- RH ---------------- */
  "planning-conges": {
    title: "Planning des congés",
    columns: [
      col("Employé", "text", { width: 22 }),
      col("Type", "text", { width: 16 }),
      col("Date début", "date", { width: 12 }),
      col("Date fin", "date", { width: 12 }),
      col("Nb jours", "formula", { width: 10, formula: "=D{row}-C{row}+1" }),
      col("Statut", "text", { width: 12 }),
    ],
    row: (i) => [`${pick(PRENOMS, i)} ${pick(NOMS, i + 2)}`, pick(TYPES_CONGE, i), dateStr(i * 5), dateStr(i * 5 + 2 + (i % 4)), "", pick(STATUTS_CONGE, i)],
    note: "« Nb jours » = Date fin − Date début + 1 (jours calendaires).",
  },
  "suivi-employes": {
    title: "Suivi des employés",
    columns: [
      col("Matricule", "text", { width: 12 }),
      col("Nom complet", "text", { width: 22 }),
      col("Poste", "text", { width: 18 }),
      col("Contrat", "text", { width: 12 }),
      col("Date d'entrée", "date", { width: 13 }),
      col("Salaire brut", "currency", { width: 14 }),
    ],
    row: (i) => [`EMP-${String(100 + i)}`, `${pick(PRENOMS, i + 1)} ${pick(NOMS, i)}`, pick(POSTES, i), pick(CONTRATS, i), dateStr(-i * 40), rnd(i + 4, 1800, 5200)],
    note: "Registre du personnel. Adaptez les colonnes via le prompt (ex : ajouter téléphone, service).",
  },
  "fiche-paie": {
    title: "Fiche de paie",
    columns: [
      col("Employé", "text", { width: 22 }),
      col("Salaire brut", "currency", { width: 14 }),
      col("Taux cotisations", "percent", { width: 14 }),
      col("Cotisations", "formula", { width: 14, formula: "=B{row}*C{row}" }),
      col("Salaire net", "formula", { width: 14, formula: "=B{row}-D{row}" }),
    ],
    row: (i) => [`${pick(PRENOMS, i)} ${pick(NOMS, i + 1)}`, rnd(i + 5, 1800, 5000), 0.22, "", ""],
    note: "« Cotisations » = Brut × Taux ; « Net » = Brut − Cotisations. Taux indicatif à ajuster.",
  },

  /* ---------------- BTP ---------------- */
  "suivi-chantier": {
    title: "Suivi de chantier",
    columns: [
      col("Tâche", "text", { width: 18 }),
      col("Responsable", "text", { width: 20 }),
      col("Date début", "date", { width: 12 }),
      col("Date fin prévue", "date", { width: 14 }),
      col("Avancement", "percent", { width: 12 }),
      col("Statut", "text", { width: 12 }),
    ],
    row: (i) => [pick(CHANTIERS_TACHES, i), `${pick(PRENOMS, i)} ${pick(NOMS, i)}`, dateStr(i * 7), dateStr(i * 7 + 10), Math.min(1, (i % 5) * 0.25), pick(STATUTS_TACHE, i)],
    note: "« Avancement » est exprimé en pourcentage (0 % à 100 %).",
  },
  "devis-travaux": {
    title: "Devis travaux",
    columns: [
      col("Désignation", "text", { width: 24 }),
      col("Unité", "text", { width: 10 }),
      col("Quantité", "number", { width: 10 }),
      col("Prix unitaire", "currency", { width: 14 }),
      col("Total HT", "formula", { width: 14, formula: "=C{row}*D{row}" }),
    ],
    row: (i) => [pick([...CHANTIERS_TACHES, ...MATERIAUX], i), pick(["m²", "m³", "u", "h", "forfait"], i), 1 + (i % 50), rnd(i + 6, 8, 1200), ""],
    note: "« Total HT » = Quantité × Prix unitaire. Pensez à ajouter la TVA via le prompt.",
  },
  "gestion-materiaux": {
    title: "Gestion des matériaux",
    columns: [
      col("Matériau", "text", { width: 20 }),
      col("Quantité", "number", { width: 10 }),
      col("Unité", "text", { width: 10 }),
      col("Prix unitaire", "currency", { width: 14 }),
      col("Coût total", "formula", { width: 14, formula: "=B{row}*D{row}" }),
      col("Fournisseur", "text", { width: 18 }),
    ],
    row: (i) => [pick(MATERIAUX, i), 5 + ((i * 11) % 200), pick(["sac", "m³", "u", "kg", "L", "m²"], i), rnd(i + 7, 1.5, 95), "", pick(["BatiPro", "MatériauxPlus", "NégoceBTP", "FournitOuest"], i)],
    note: "« Coût total » = Quantité × Prix unitaire.",
  },

  /* ---------------- AUTRE ---------------- */
  "tableau-generique": {
    title: "Tableau personnalisé",
    columns: [
      col("Désignation", "text", { width: 24 }),
      col("Catégorie", "text", { width: 16 }),
      col("Quantité", "number", { width: 10 }),
      col("Montant unitaire", "currency", { width: 16 }),
      col("Total", "formula", { width: 14, formula: "=C{row}*D{row}" }),
    ],
    row: (i) => [`Élément ${i + 1}`, pick(["A", "B", "C"], i), 1 + (i % 20), rnd(i + 8, 5, 500), ""],
    note: "Tableau générique. Décrivez précisément vos colonnes dans le prompt pour l'adapter.",
  },
  budget: {
    title: "Suivi de budget",
    columns: [
      col("Date", "date", { width: 12 }),
      col("Libellé", "text", { width: 24 }),
      col("Catégorie", "text", { width: 16 }),
      col("Recette", "currency", { width: 12 }),
      col("Dépense", "currency", { width: 12 }),
      col("Solde", "formula", { width: 12, formula: "=D{row}-E{row}" }),
    ],
    row: (i) => {
      const recette = i % 3 === 0 ? rnd(i + 9, 100, 3000) : 0;
      const depense = i % 3 === 0 ? 0 : rnd(i + 10, 20, 1500);
      return [dateStr(i * 3), `Opération ${i + 1}`, pick(["Salaire", "Loyer", "Fournitures", "Transport", "Divers"], i), recette, depense, ""];
    },
    note: "« Solde » = Recette − Dépense (par ligne).",
  },
  planning: {
    title: "Planning / Tâches",
    columns: [
      col("Tâche", "text", { width: 26 }),
      col("Responsable", "text", { width: 18 }),
      col("Échéance", "date", { width: 12 }),
      col("Priorité", "text", { width: 12 }),
      col("Statut", "text", { width: 12 }),
    ],
    row: (i) => [`Tâche ${i + 1}`, `${pick(PRENOMS, i)} ${pick(NOMS, i)}`, dateStr(i * 4), pick(["Haute", "Moyenne", "Basse"], i), pick(STATUTS_TACHE, i)],
    note: "Adaptez priorités et statuts à votre organisation via le prompt.",
  },
};

/* ----------------------------------------------------------------------------
 * Parsing du prompt libre
 * ------------------------------------------------------------------------- */

export interface ParsedPrompt {
  rowCount: number;
  addTVA: boolean;
  tvaRate: number;
  extraColumns: string[];
}

export function parsePrompt(prompt: string): ParsedPrompt {
  const p = (prompt || "").toLowerCase();
  const result: ParsedPrompt = { rowCount: 10, addTVA: false, tvaRate: 0.2, extraColumns: [] };

  // Nombre de lignes : "20 lignes", "prévoir 30 lignes", "15 entrées"
  const rowMatch = p.match(/(\d+)\s*(lignes?|entr[ée]es?|enregistrements?|rang[ée]es?)/);
  if (rowMatch) {
    const n = parseInt(rowMatch[1], 10);
    if (n > 0) result.rowCount = Math.min(n, 200); // plafond raisonnable
  }

  // TVA : "ajouter une colonne TVA", "avec TVA 5.5%", "tva à 10%"
  if (/\btva\b/.test(p)) {
    result.addTVA = true;
    const rateMatch = p.match(/tva[^\d]{0,12}(\d{1,2}(?:[.,]\d{1,2})?)\s*%/);
    if (rateMatch) {
      result.tvaRate = parseFloat(rateMatch[1].replace(",", ".")) / 100;
    } else {
      const pctMatch = p.match(/(\d{1,2}(?:[.,]\d{1,2})?)\s*%/);
      if (pctMatch) result.tvaRate = parseFloat(pctMatch[1].replace(",", ".")) / 100;
    }
  }

  // Colonnes additionnelles : "ajouter une colonne téléphone", "ajoute la colonne remise"
  const colRegex = /ajout(?:er|e|ez)?\s+(?:une?\s+|la\s+|les\s+)?colonnes?\s+([a-zàâäéèêëïîôöùûüç0-9'’\-\s]+?)(?:[,.;]|\set\b|\bavec\b|$)/g;
  let m: RegExpExecArray | null;
  while ((m = colRegex.exec(p)) !== null) {
    const raw = m[1].trim();
    if (raw && !/tva/.test(raw)) {
      const clean = raw.charAt(0).toUpperCase() + raw.slice(1);
      if (clean.length <= 30) result.extraColumns.push(clean);
    }
  }

  return result;
}

/* ----------------------------------------------------------------------------
 * Génération principale
 * ------------------------------------------------------------------------- */

export function generateTable(taskId: string, prompt: string): GeneratedTable {
  const template = TEMPLATES[taskId] || TEMPLATES["tableau-generique"];
  const parsed = parsePrompt(prompt);

  // Copie des colonnes
  let columns: ColumnDef[] = template.columns.map((c) => ({ ...c }));

  // Colonnes additionnelles texte demandées dans le prompt
  for (const extra of parsed.extraColumns) {
    columns.push(col(extra, "text", { width: 16 }));
  }

  // Génération des lignes de base
  const rows: (string | number)[][] = [];
  for (let i = 0; i < parsed.rowCount; i++) {
    const baseRow = template.row(i, template.columns);
    // compléter pour les colonnes additionnelles texte
    while (baseRow.length < columns.length) baseRow.push("");
    rows.push(baseRow);
  }

  // Gestion TVA : on s'appuie sur une colonne "Total HT" ou "Total" si présente
  if (parsed.addTVA) {
    const htName = columns.find((c) => /total ht/i.test(c.name))?.name
      || columns.find((c) => /^total$/i.test(c.name))?.name
      || columns.find((c) => /total/i.test(c.name))?.name;
    if (htName) {
      const htIndex = idxOf(columns, htName);
      const htLetter = colLetter(htIndex);
      const pct = Math.round(parsed.tvaRate * 100);
      // Colonne TVA (formule sur le HT)
      const tvaIndex = columns.length;
      const tvaLetter = colLetter(tvaIndex);
      columns.push(col(`TVA (${pct}%)`, "formula", { width: 12, formula: `=${htLetter}{row}*${parsed.tvaRate}` }));
      // Colonne Total TTC
      columns.push(col("Total TTC", "formula", { width: 14, formula: `=${htLetter}{row}+${tvaLetter}{row}` }));
      // compléter les lignes existantes
      for (const r of rows) {
        while (r.length < columns.length) r.push("");
      }
    }
  }

  let note = template.note;
  if (parsed.addTVA && columns.some((c) => /^tva/i.test(c.name))) {
    note = `${note ? note + " " : ""}TVA appliquée au taux de ${Math.round(parsed.tvaRate * 100)} % avec colonne « Total TTC ».`;
  }
  if (parsed.extraColumns.length) {
    note = `${note ? note + " " : ""}Colonne(s) ajoutée(s) : ${parsed.extraColumns.join(", ")}.`;
  }

  return {
    title: template.title,
    columns,
    rows,
    note,
  };
}
